import data from './seoPlaces.json';

/**
 * Place landing pages — /flats-in-<slug>.
 *
 * <p>One table, three readers: this module for the running app, seoMeta for the
 * document head, and scripts/prerender.js which requires the JSON directly. The
 * JSON itself is generated by scripts/generate-seo-places.js from live listing
 * counts; see that file for why a page is only indexed when inventory backs it.
 *
 * <p>Note the split between <em>resolving</em> and <em>indexing</em>. Every
 * place here resolves: the URL loads the search with that location prefilled,
 * so a link works whether it came from Google, a WhatsApp share or an ad. Only
 * places with `index: true` are put in the sitemap and prerendered. A page that
 * exists and works is not the same as a page we ask Google to rank, and
 * conflating the two is how a site ends up publishing doorway pages.
 */

export const PREFIX = '/flats-in-';

const BY_SLUG = data.places.reduce((acc, p) => {
    acc[p.slug] = p;
    return acc;
}, {});

/** All places, whether or not they are indexed. */
export const ALL_PLACES = data.places;

/** Only the places we ask search engines to rank, biggest inventory first. */
export const INDEXED_PLACES = data.places
    .filter((p) => p.index)
    .sort((a, b) => b.listings - a.listings);

export const pathForPlace = (place) => `${PREFIX}${place.slug}`;

/** Resolves a pathname to a place, or null. Tolerates a trailing slash. */
export const placeForPath = (pathname) => {
    if (!pathname || !pathname.startsWith(PREFIX)) return null;
    const slug = pathname.slice(PREFIX.length).replace(/\/+$/, '');
    return BY_SLUG[slug] || null;
};

/**
 * The head for a place page, in the shape seoRoutes.json entries use so
 * metaForPath can return either kind without the caller caring. The strings
 * come from the generated table — see generate-seo-places.js — so this module
 * and the build-time prerender cannot drift apart.
 */
export const placeMeta = (place) => ({
    path: pathForPlace(place),
    crumb: place.name,
    title: place.title,
    description: place.description,
    heading: place.heading,
    place,
});

export default { ALL_PLACES, INDEXED_PLACES, placeForPath, placeMeta, pathForPlace, PREFIX };
